import {
	Box,
	Button,
	Col,
	Component,
	Divider,
	Grid,
	Heading,
	Icons,
	Inline,
	Select,
	Stack,
	Stars,
	Table,
	Text,
	joinAttributes,
} from "@sembark-travel/ui-base"
import {
	Form,
	GetFieldValue,
	MarkdownInputField,
	RadioInputField,
	SelectField,
	SelectInputField,
	SubmissionError,
	SwitchInputField,
	TextInputField,
	isTruthy,
	useFieldValue,
	useForm,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import {
	BORDER_WIDTH_OPTIONS,
	TBrandWiseColors,
	TFontFamily,
	THotelImagePlacementOptions,
	THotelImageVariant,
	THotelSelectedImageVariant,
	THotelTableVariant,
	TImageOptions,
	TImageStackedOptions,
	TSidebySideVariant,
	TStackedVariant,
	TTripQuotePdfTemplate,
} from "./store"
import { useCallback, useMemo } from "react"
import {
	GreetingsTemplateField,
	JustifiedContentToggleField,
	SelectFontFamilyField,
} from "./SharedFormFields"
import { hexToRgba } from "../utils"
import { ReorderSections } from "./ReorderSections"
import { FontFamily } from "./FontFamily"

export type TEmailPdfTemplateCreatePayload = Omit<
	TTripQuotePdfTemplate,
	"id"
> & {
	variables: Array<{
		id: string
		name: string
		description: string
		placeholder: string
	}>
	font_families: Array<TFontFamily>
	border_style_options?: Array<string>
	section_title_settings_options?: {
		background: Array<string>
		border: Array<string>
		rounded: Array<string>
		textAlign: Array<string>
	}
	transport_and_activity_section_settings: {
		variables: Array<string>
		default_variant: string
		variants: Array<{
			id: string
			name: string
			description: string
		}>
		show_activity_start_time: 0 | 1 | "0" | "1"
	}
	pricing_section_settings?: {
		hide_quote_price?: 0 | 1 | "0" | "1"
		show_package_price_below_hotel: 0 | 1 | "0" | "1"
		show_component_wise_prices: 0 | 1 | "0" | "1"
	}
	hotel_section_settings?: {
		similar_word_in_hotel?: 0 | 1 | "0" | "1"
		title: string
		variant: THotelSelectedImageVariant | THotelTableVariant
		variants: Array<THotelImageVariant | THotelTableVariant>
	}
	hotel_variation: THotelImageVariant | THotelTableVariant
	hotel_variation_image_position: THotelImagePlacementOptions
	daywise_variation: TStackedVariant | TSidebySideVariant
	inclusions_exclusions_section_settings: {
		title: string
	}
}

export function EditTripQuotePdfTemplateForm({
	onCancel,
	onSubmit,
	initialValues,
	tenantColor,
}: {
	onCancel: () => void
	onSubmit: (
		data: Pick<
			TEmailPdfTemplateCreatePayload,
			"name" | "greetings" | "section_ordering_settings"
		> & {
			hotel_section_settings?: Omit<
				NonNullable<TEmailPdfTemplateCreatePayload["hotel_section_settings"]>,
				"variants"
			>

			package_preview: Omit<
				TEmailPdfTemplateCreatePayload["package_preview"],
				"variants"
			>

			daywise_schedule_section_settings?: Omit<
				NonNullable<
					TEmailPdfTemplateCreatePayload["daywise_schedule_section_settings"]
				>,
				"variants"
			>
		} & Pick<
				TTripQuotePdfTemplate,
				"transport_and_activity_section_settings"
			> & {
				settings: Omit<
					TEmailPdfTemplateCreatePayload["settings"],
					"fontFamily" | "headingFontFamily"
				> & {
					fontFamily: string
					headingFontFamily: string
				}
				section_title_settings: Omit<
					TEmailPdfTemplateCreatePayload["section_title_settings"],
					"sectionHeadingFontFamily"
				> & {
					sectionHeadingFontFamily: string
				}
				inclusions_exclusions_section_settings?: TEmailPdfTemplateCreatePayload["inclusions_exclusions_section_settings"]
			}
	) => Promise<unknown>
	initialValues: TEmailPdfTemplateCreatePayload
	tenantColor: TBrandWiseColors
	fontFamilyNotice?: string
}) {
	const {
		name,
		greetings,
		settings,
		variables,
		font_families,
		transport_and_activity_section_settings,
		daywise_schedule_section_settings,
		other_info_section_settings,
		terms_section_settings,
		hero_section_settings,
		border_style_options,
		section_title_settings,
		section_title_settings_options,
		section_ordering_settings,
		pricing_section_settings,
		hotel_section_settings,
		package_preview,
		inclusions_exclusions_section_settings,
	} = initialValues
	return (
		<Form<{
			name: string
			settings: TEmailPdfTemplateCreatePayload["settings"]
			section_title_settings: TEmailPdfTemplateCreatePayload["section_title_settings"]
			daywise_schedule_section_settings?: TEmailPdfTemplateCreatePayload["daywise_schedule_section_settings"]
			terms_section_settings?: TEmailPdfTemplateCreatePayload["terms_section_settings"]
			other_info_section_settings?: TEmailPdfTemplateCreatePayload["other_info_section_settings"]
			hero_section_settings?: TEmailPdfTemplateCreatePayload["hero_section_settings"]
			section_ordering_settings?: TEmailPdfTemplateCreatePayload["section_ordering_settings"]
			greetings: string
			transport_and_activity_section_settings: Omit<
				TEmailPdfTemplateCreatePayload["transport_and_activity_section_settings"],
				"variant"
			> & {
				variant?: TEmailPdfTemplateCreatePayload["transport_and_activity_section_settings"]["variants"][number]
			}
			pricing_section_settings?: TEmailPdfTemplateCreatePayload["pricing_section_settings"]
			hotel_section_settings?: TEmailPdfTemplateCreatePayload["hotel_section_settings"]
			hotel_variation_image_position: TEmailPdfTemplateCreatePayload["hotel_variation_image_position"]
			hotel_variation: TEmailPdfTemplateCreatePayload["hotel_variation"]
			package_preview: TEmailPdfTemplateCreatePayload["package_preview"]
			package_variation: TEmailPdfTemplateCreatePayload["package_preview"]["variant"]
			daywise_variation: TEmailPdfTemplateCreatePayload["daywise_variation"]
			daywise_variation_image_position: TImageOptions | TImageStackedOptions
			inclusions_exclusions_section_settings?: TEmailPdfTemplateCreatePayload["inclusions_exclusions_section_settings"]
		}>
			initialValues={{
				name,
				settings: {
					...settings,
					showSingleHeaderFooter: settings.showSingleHeaderFooter,
					watermarkOpacity: Math.round(
						((settings.watermarkOpacity || 0) / 0.02) * 100
					),
					backgroundOpacity: (settings.backgroundOpacity || 0) * 100,
				},
				greetings,
				transport_and_activity_section_settings: {
					...transport_and_activity_section_settings,
					variant: transport_and_activity_section_settings.variants.find(
						(v) => transport_and_activity_section_settings.variant === v.id
					),
				},
				other_info_section_settings,
				terms_section_settings,
				hero_section_settings,
				section_title_settings,
				section_ordering_settings,
				package_preview,
				daywise_schedule_section_settings: daywise_schedule_section_settings
					? {
							...daywise_schedule_section_settings,
							// transform for our radio items
							hide_travel_distance: isTruthy(
								daywise_schedule_section_settings.hide_travel_distance
							)
								? "1"
								: "0",
							show_same_day_service_together: isTruthy(
								daywise_schedule_section_settings.show_same_day_service_together
							)
								? "1"
								: "0",
						}
					: undefined,
				pricing_section_settings: pricing_section_settings && {
					...pricing_section_settings,
					show_package_price_below_hotel: isTruthy(
						pricing_section_settings.show_package_price_below_hotel
					)
						? "1"
						: "0",
					show_component_wise_prices: isTruthy(
						pricing_section_settings.show_component_wise_prices
					)
						? "1"
						: "0",
					hide_quote_price: isTruthy(pricing_section_settings.hide_quote_price)
						? "1"
						: "0",
				},
				hotel_section_settings: hotel_section_settings
					? {
							...hotel_section_settings,
							// transform for our radio items
							similar_word_in_hotel: isTruthy(
								hotel_section_settings.similar_word_in_hotel
							)
								? "1"
								: "0",
						}
					: undefined,
				daywise_variation: daywise_schedule_section_settings?.variants.find(
					(v) => v.id === daywise_schedule_section_settings.variant.id
				),
				daywise_variation_image_position:
					daywise_schedule_section_settings?.variants
						.find((v) => v.id === daywise_schedule_section_settings.variant.id)
						?.configurations?.find(
							(c) =>
								c.id ===
								daywise_schedule_section_settings.variant.configurations[0].id
						)
						?.options?.find(
							(opt) =>
								opt.id ===
								daywise_schedule_section_settings.variant.configurations[0]
									.value
						),
				hotel_variation: hotel_section_settings?.variants.find(
					(v) => v.id === hotel_section_settings.variant.id
				),
				hotel_variation_image_position:
					hotel_section_settings?.variant.id === "table_view"
						? undefined
						: hotel_section_settings?.variants
								?.find((v) => v.id === hotel_section_settings?.variant.id)
								?.configurations?.find((c) => c.id === "image_placement")
								?.options?.find(
									(opt) =>
										opt.id ===
										hotel_section_settings?.variant.configurations.find(
											(c) => c.id === "image_placement"
										)?.value
								),
				package_variation: package_preview.variant,
				inclusions_exclusions_section_settings,
			}}
			onSubmit={withServerErrors(
				async ({
					settings: {
						justifiedText,
						fontFamily,
						headingFontFamily,
						borderStyle,
						watermarkOpacity,
						backgroundOpacity,
						...otherSettings
					},
					transport_and_activity_section_settings,
					...values
				}) => {
					const { greetings } = values
					const contentWithPlaceholders = [
						greetings,
						otherSettings.watermarkText || "",
					].filter((s) => Boolean(s))
					const invalidPlaceholders: Array<string> = []
					contentWithPlaceholders.forEach((content) => {
						if (!content) return
						// check for non-existing variable placeholders
						const placeholderRegex = /\{\{\s*([\w\d]+)\s*\}\}/gi
						let result
						while ((result = placeholderRegex.exec(content)) !== null) {
							const p = result[1]
							if (
								!variables.find((v) => v.placeholder === "{{" + p.trim() + "}}")
							) {
								invalidPlaceholders.push(p)
							}
						}
					})
					if (invalidPlaceholders.length) {
						throw new Error(
							`Invalid placeholder variables: ${invalidPlaceholders.join(
								", "
							)}. Only available variables are allowed.`
						)
					}
					borderStyle = borderStyle ? borderStyle.toLowerCase() : undefined
					const {
						hotel_variation,
						hotel_variation_image_position,
						daywise_variation,
						daywise_variation_image_position,
						package_variation,
						...restValues
					} = values
					await onSubmit({
						...restValues,
						section_title_settings: {
							...values.section_title_settings,
							sectionHeadingFontFamily:
								values.section_title_settings.sectionHeadingFontFamily.id,
						},
						settings: {
							...otherSettings,
							borderTopStyle: borderStyle,
							borderRightStyle: borderStyle,
							borderBottomStyle: borderStyle,
							borderLeftStyle: borderStyle,
							fontFamily: fontFamily.id,
							justifiedText: isTruthy(justifiedText) ? 1 : 0,
							watermarkOpacity: watermarkOpacity
								? (watermarkOpacity / 100) * 0.02
								: 0,
							backgroundOpacity: backgroundOpacity
								? backgroundOpacity / 100
								: 0,
							headingFontFamily: headingFontFamily.id,
						},
						transport_and_activity_section_settings: {
							title: transport_and_activity_section_settings.title,
							variant:
								transport_and_activity_section_settings.variant?.id ||
								transport_and_activity_section_settings.variants[0].id,
							hide_transport_summary_for_same_cabs:
								transport_and_activity_section_settings.hide_transport_summary_for_same_cabs,
							terms: transport_and_activity_section_settings.terms,
							show_activity_start_time:
								transport_and_activity_section_settings.show_activity_start_time,
						},
						inclusions_exclusions_section_settings:
							values.inclusions_exclusions_section_settings,

						section_ordering_settings: values.section_ordering_settings
							? values.section_ordering_settings
							: section_ordering_settings,
						hotel_section_settings: {
							title:
								values.hotel_section_settings?.title ||
								hotel_section_settings?.title ||
								"Hotels / Accommodations",
							similar_word_in_hotel: isTruthy(
								values.hotel_section_settings?.similar_word_in_hotel
							)
								? 1
								: 0,
							variant:
								values.hotel_variation.id === "image_and_text"
									? {
											id: values.hotel_variation.id,
											name: values.hotel_variation.name,
											configurations: values.hotel_variation.configurations.map(
												(c) => ({
													id: c.id,
													name: c.name,
													value:
														c.options.find(
															(o) =>
																o.id ===
																values.hotel_variation_image_position.id
														)?.id ?? values.hotel_variation_image_position.id,
												})
											),
										}
									: {
											id: values.hotel_variation.id,
											name: values.hotel_variation.name,
											configurations: [],
										},
						},
						package_preview: {
							variant: values.package_variation,
						},
						daywise_schedule_section_settings: {
							title:
								values.daywise_schedule_section_settings?.title ||
								daywise_schedule_section_settings?.title ||
								"Day Wise Itinerary",
							hide_travel_distance: isTruthy(
								values.daywise_schedule_section_settings?.hide_travel_distance
							)
								? 1
								: 0,
							show_same_day_service_together: isTruthy(
								values.daywise_schedule_section_settings
									?.show_same_day_service_together
							)
								? 1
								: 0,
							variant:
								values.daywise_variation.id === "stacked"
									? {
											id: "stacked",
											name: values.daywise_variation.name,
											configurations: [
												{
													id: "image_stacked",
													name: values.daywise_variation.configurations[0].name,
													value: values.daywise_variation_image_position.id as
														| "align_to_service"
														| "full_width",
												},
											],
										}
									: {
											id: "side_by_side",
											name: values.daywise_variation.name,
											configurations: [
												{
													id: "image",
													name: values.daywise_variation.configurations[0].name,
													value: values.daywise_variation_image_position.id as
														| "right"
														| "left"
														| "alternate",
												},
											],
										},
						},
					})
					showSnackbar("Template details saved")
				}
			)}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit, form }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						<Grid gap="6">
							<Col sm={12} md={4}>
								<Stack gap="1">
									<Heading fontSize="md">Settings</Heading>
									<Text color="muted">
										Configure template settings like fonts and text alignment.
									</Text>
								</Stack>
							</Col>
							<Col>
								<Stack gap="4">
									<SelectFontFamilyField
										label="Font Family"
										name="settings.fontFamily"
										fontFamilies={font_families}
										help="Used for body text, descriptions, and supporting details throughout the PDF."
									/>
									<SelectFontFamilyField
										label="Heading Font Family"
										name="settings.headingFontFamily"
										fontFamilies={font_families}
										help="Used for primary headings within each section - such as hotel names, day titles, and activity headings."
									/>
									<JustifiedContentToggleField />
									<TextInputField
										name="settings.watermarkText"
										label="Watermark Text"
										type="text"
										placeholder="e.g {{ BRAND_NAME }} • {{ TRIP_ID }} •"
										help={() => {
											return (
												<Stack fontSize="sm">
													<Text>
														You can use following placeholder variables in the
														watermark
													</Text>
													<Box>
														{joinAttributes(
															...variables.map((variable) => (
																<Button
																	key={variable.id}
																	inline
																	title={variable.description}
																	onClick={(
																		e: React.MouseEvent<HTMLButtonElement>
																	) => {
																		e.preventDefault()
																		form.change(
																			"settings.watermarkText" as never,
																			((form.getState().values.settings
																				?.watermarkText || "") +
																				` ${variable.placeholder}`) as never
																		)
																		showSnackbar("Added variable to content.")
																	}}
																>
																	{variable.placeholder}
																</Button>
															))
														)}
													</Box>
												</Stack>
											)
										}}
									/>
									{/* opacity of waterMark */}
									<TextInputField
										name="settings.watermarkOpacity"
										label="Watermark Opacity"
										type="number"
										min={0}
										max={100}
										help={({ value }) => {
											const opacityInput = Number(value) || 0
											const opacity = (opacityInput / 100) * 0.02
											return (
												<>
													{opacityInput === 0 ? (
														<Text>
															<b>Note : </b> Watermark will be not be shown as
															the opacity is zero
														</Text>
													) : opacityInput > 100 || opacityInput < 0 ? (
														<Text color="danger">
															Please enter a value between 0 and 100
														</Text>
													) : (
														<Stack gap="1">
															<Text fontSize="sm" color="muted">
																Section Watermark Preview
															</Text>
															<Inline
																paddingX="4"
																paddingY="4"
																rounded="md"
																borderWidth="1"
																flexWrap="wrap"
																gap="4"
																style={{
																	backgroundImage:
																		"radial-gradient(rgb(68, 76, 247) 0.5px, rgb(255, 255, 255) 0.5px)",
																	backgroundSize: "10px 10px",
																}}
															>
																<div
																	style={{
																		display: "inline-block",
																		width: "100px",
																		height: "30px",
																		background: "black",
																		opacity: opacity,
																	}}
																/>
																<div
																	style={{
																		display: "inline-block",
																		width: "100px",
																		height: "30px",
																		background: "black",
																		opacity: opacity,
																	}}
																/>
																<div
																	style={{
																		display: "inline-block",
																		width: "100px",
																		height: "30px",
																		background: "black",
																		opacity: opacity,
																	}}
																/>
															</Inline>
														</Stack>
													)}
												</>
											)
										}}
									/>
								</Stack>
							</Col>
						</Grid>
						<Divider />
						<Grid gap="6">
							<Col sm={12} md={4}>
								<Stack gap="1">
									<Heading fontSize="md">Hide Branding</Heading>
									<Text color="muted">
										Choose whether to show or hide branding on the quote PDF.
									</Text>
								</Stack>
							</Col>
							<Col>
								<Stack gap="4">
									<SwitchInputField
										name="settings.hideBrandingB2B"
										label="Hide Branding for B2B"
										help="Select the checkbox to remove the branding for B2B in Quote PDF"
									/>
									<SwitchInputField
										name="settings.hideBrandingB2C"
										label="Hide Branding for B2C"
										help="Select the checkbox to remove the branding for B2C in Quote PDF"
									/>
									<SwitchInputField
										name="settings.showHeaderBannerImg"
										label="Header Banner Image"
										help="Show/Hide brand header banner image in Quote PDF"
									/>
									<SwitchInputField
										name="settings.showFooterBannerImg"
										label="Footer Banner Image"
										help="Show/Hide brand's footer banner image in Quote PDF"
									/>
									<SwitchInputField
										name="settings.showSingleHeaderFooter"
										label="Show header on first page & footer on last page"
										help="Enable to display the header on the first page and the footer on the last page in Quote PDF."
									/>
								</Stack>
							</Col>
						</Grid>
						{border_style_options?.length ? (
							<>
								<Divider sm />
								<Grid gap="6">
									<Col sm={12} md={4}>
										<Stack gap="1">
											<Heading fontSize="md">
												Border and Background Setting
											</Heading>
											<Text color="muted">Customize the background.</Text>
										</Stack>
									</Col>
									<Col>
										<Grid noGutters>
											<Col>
												<Inline flexWrap="wrap" gap="4">
													<GetBorderWidthFieldValue settingsName="settings">
														{({ hasBorder, onChange }) => (
															<SelectField
																select={Select}
																name="settings.borderStyle"
																label="Border Style"
																placeholder="e.g. None"
																options={border_style_options}
																onChange={(value, name) => {
																	form.change(name as never, value as never)
																	if (!value || value === "none") {
																		onChange(0)
																	} else {
																		if (!hasBorder) {
																			onChange(4)
																		}
																	}
																}}
															/>
														)}
													</GetBorderWidthFieldValue>
													<GetFieldValue<
														string | undefined
													> name="settings.borderStyle">
														{({ value: borderStyle }) =>
															!borderStyle || borderStyle === "none" ? null : (
																<Inline gap="4" flexWrap={"wrap"}>
																	{["Top", "Bottom", "Left", "Right"].map(
																		(side, index) => {
																			const fieldName = `settings.border${side}Width`
																			return (
																				<SelectInputField
																					key={index}
																					name={fieldName}
																					max={10}
																					min={0}
																					step={1}
																					type="number"
																					label={side + " Width"}
																					color={"muted"}
																					placeholder="e.g. 0"
																					onChange={(e) => {
																						const value = e.currentTarget.value
																						form.change(
																							e.currentTarget.name as never,
																							value as never
																						)
																						if (Number(value || "0")) {
																							if (
																								!borderStyle ||
																								borderStyle.toLowerCase() ===
																									"none"
																							) {
																								form.change(
																									"settings.borderStyle" as never,
																									border_style_options[1] as never
																								)
																							}
																						}
																					}}
																				>
																					{BORDER_WIDTH_OPTIONS.map((width) => (
																						<option value={width} key={width}>
																							{width}
																						</option>
																					))}
																				</SelectInputField>
																			)
																		}
																	)}
																</Inline>
															)
														}
													</GetFieldValue>
												</Inline>

												<Inline paddingY={"4"}>
													<TextInputField
														name="settings.backgroundOpacity"
														label="Background Color Opacity"
														placeholder="eg: 90 "
														type="number"
														min={0}
														max={100}
														maxWidth={"3xl"}
														help={({ value }) =>
															Number(value) === 0 ? (
																<Text>
																	<b>Note : </b> On opacity zero background will
																	be gray
																</Text>
															) : Number(value) > 100 || Number(value) < 0 ? (
																<Text color="danger">
																	Please enter a value between 0 and 100
																</Text>
															) : null
														}
													/>
												</Inline>
											</Col>
											<Col>
												<GetFieldValue<typeof settings> name="settings">
													{({ value: setting }) => {
														const opacity = setting.backgroundOpacity
															? setting.backgroundOpacity / 100
															: 0
														return setting.borderStyle !== "none" || opacity ? (
															<Box height={"full"}>
																<Text color={"muted"}>
																	Border and Color Preview
																</Text>
																<Box
																	paddingTop={"2"}
																	style={{
																		width: "250px",
																		height: "375px",
																		backgroundColor: hexToRgba(
																			tenantColor.brand_wise_color_shades[0]
																				.color_shades?.["shade-50"] ||
																				"transparent",
																			(setting.backgroundOpacity || 0) / 100
																		),
																		borderColor:
																			tenantColor.brand_wise_color_shades[0]
																				.color_shades?.["shade-400"],
																		borderStyle: setting.borderStyle,
																		borderBottomWidth: `${setting.borderBottomWidth}px`,
																		borderTopWidth: `${setting.borderTopWidth}px`,
																		borderLeftWidth: `${setting.borderLeftWidth}px`,
																		borderRightWidth: `${setting.borderRightWidth}px`,
																	}}
																/>
															</Box>
														) : null
													}}
												</GetFieldValue>
											</Col>
										</Grid>
									</Col>
								</Grid>
							</>
						) : null}
						{section_title_settings_options ? (
							<>
								<Divider sm />
								<Grid gap="6">
									<Col sm={12} md={4}>
										<Stack gap="1">
											<Heading fontSize="md">Section Title Settings</Heading>
											<Text color="muted">
												Customize the appearance of section's (e.g.
												Accommodation, Transportation etc.) title
											</Text>
										</Stack>
									</Col>
									<Col>
										<Stack gap="4">
											<Inline gap="4" flexWrap="wrap">
												<SelectInputField
													name="section_title_settings.background"
													label="Background Color"
												>
													{section_title_settings_options.background.map(
														(s) => (
															<option value={s} key={s}>
																{s}
															</option>
														)
													)}
												</SelectInputField>
												<SelectInputField
													name="section_title_settings.border"
													label="Border Position"
												>
													{section_title_settings_options.border.map((s) => (
														<option value={s} key={s}>
															{s}
														</option>
													))}
												</SelectInputField>
												<SelectInputField
													name="section_title_settings.rounded"
													label="Roundness"
												>
													{section_title_settings_options.rounded.map((s) => (
														<option value={s} key={s}>
															{s}
														</option>
													))}
												</SelectInputField>
												<SelectInputField
													name="section_title_settings.textAlign"
													label="Text Align"
												>
													{section_title_settings_options.textAlign.map((s) => (
														<option value={s} key={s}>
															{s}
														</option>
													))}
												</SelectInputField>
												<SelectFontFamilyField
													label="Section Heading Font Family"
													name="section_title_settings.sectionHeadingFontFamily"
													fontFamilies={font_families}
												/>
											</Inline>
											<GetFieldValue<
												TEmailPdfTemplateCreatePayload["section_title_settings"]
											> name="section_title_settings">
												{({ value: section_title_settings }) => {
													if (!section_title_settings) return null
													const dark_fill =
														tenantColor.brand_wise_color_shades[0]
															.color_shades?.["shade-700"]
													const borderColor =
														tenantColor.brand_wise_color_shades[0]
															.color_shades?.["shade-600"] || "emphasis"
													const light_fill =
														tenantColor.brand_wise_color_shades[0]
															.color_shades?.["shade-200"]
													const textColor =
														tenantColor.brand_wise_color_shades[0]
															.color_shades?.["shade-800"]
													return (
														<Stack gap="1">
															<Text fontSize="sm" color="muted">
																Section Title Preview
															</Text>
															<Box
																paddingX="4"
																paddingY="2"
																fontWeight="semibold"
																style={{
																	borderColor: borderColor,
																	color:
																		section_title_settings.background ===
																		"dark_fill"
																			? "#fff"
																			: textColor,
																	backgroundColor:
																		section_title_settings.background === "fill"
																			? light_fill
																			: section_title_settings.background ===
																				  "dark_fill"
																				? dark_fill
																				: "#fff",
																}}
																roundedRight={
																	section_title_settings.rounded === "right"
																		? "md"
																		: undefined
																}
																roundedLeft={
																	section_title_settings.rounded === "left"
																		? "md"
																		: undefined
																}
																roundedBottom={
																	section_title_settings.rounded === "bottom"
																		? "md"
																		: undefined
																}
																roundedTop={
																	section_title_settings.rounded === "top"
																		? "md"
																		: undefined
																}
																rounded={
																	section_title_settings.rounded === "all"
																		? "md"
																		: undefined
																}
																borderLeftWidth={
																	section_title_settings.border === "left" ||
																	section_title_settings.border === "all" ||
																	section_title_settings.border === "vertical"
																		? "4"
																		: "0"
																}
																borderRightWidth={
																	section_title_settings.border === "right" ||
																	section_title_settings.border === "all" ||
																	section_title_settings.border === "vertical"
																		? "4"
																		: "0"
																}
																borderTopWidth={
																	section_title_settings.border === "top" ||
																	section_title_settings.border === "all" ||
																	section_title_settings.border === "horizontal"
																		? "4"
																		: "0"
																}
																borderBottomWidth={
																	section_title_settings.border === "bottom" ||
																	section_title_settings.border === "all" ||
																	section_title_settings.border === "horizontal"
																		? "4"
																		: "0"
																}
																textAlign={
																	section_title_settings.textAlign !== "auto"
																		? section_title_settings.textAlign
																		: undefined
																}
															>
																<FontFamily
																	family={
																		section_title_settings.sectionHeadingFontFamily
																	}
																>
																	Sample Title Text
																</FontFamily>
															</Box>
														</Stack>
													)
												}}
											</GetFieldValue>
										</Stack>
									</Col>
								</Grid>
							</>
						) : null}
						<Divider sm />
						<GetFieldValue<typeof settings> name="settings">
							{({ value }) => (
								<Stack gap="2">
									<Grid gap="6">
										<Col sm={12} md={4}>
											<Stack gap="1">
												<Heading fontSize="md">Template for Greetings</Heading>
												<Text color="muted">
													Customize the greetings message according to your
													needs.
												</Text>
											</Stack>
										</Col>
										<Col>
											<GreetingsTemplateField
												fontFamily={value.fontFamily}
												justifiedText={value.justifiedText}
												variables={variables}
											/>
										</Col>
									</Grid>
								</Stack>
							)}
						</GetFieldValue>
						<Divider sm />
						<Stack gap="2">
							<Grid gap="6">
								<Col sm={12} md={4}>
									<Stack gap="1">
										<Heading fontSize="md">
											Settings for Package Overview Section
										</Heading>
										<Text color="muted">
											Customize the section style and other settings.
										</Text>
									</Stack>
								</Col>

								<Col>
									<Grid gap="4">
										<Col sm={6} paddingX="0" md={3}>
											<SelectField
												name="package_variation"
												select={Select}
												label="Package Overview Style"
												options={package_preview.variants}
												clearable={false}
											/>
										</Col>
										<Col sm={12} paddingX="0" md={7}>
											<GetFieldValue<
												TEmailPdfTemplateCreatePayload["package_preview"]["variant"]
											> name="package_variation">
												{({ value: package_variation }) => (
													<Stack gap="1">
														<Box>
															<Text fontWeight="semibold">
																Package Overview Preview
															</Text>
														</Box>
														{package_variation.id ===
															"show_details_in_table" && (
															<PackagePreviewTableView />
														)}
														{package_variation.id === "default" && (
															<PackagePreviewDefaultView />
														)}
													</Stack>
												)}
											</GetFieldValue>
										</Col>
									</Grid>
								</Col>
							</Grid>
						</Stack>
						<Divider marginY="0" />
						<Grid gap="6">
							<Col sm={12} md={4}>
								<Stack gap="1">
									<Heading fontSize="md">Settings for Hotels Section</Heading>
									<Text color="muted">
										Customize the settings of Hotels section
									</Text>
								</Stack>
							</Col>
							<Col>
								<Stack gap="4">
									<TextInputField
										label="Hotel Section Title"
										name="hotel_section_settings.title"
										placeholder="e.g. Hotel & Accommodation"
									/>

									<SwitchInputField
										name="hotel_section_settings.similar_word_in_hotel"
										label="Add Similar Word in Hotels Name"
										help="This can be helpful if you want to add similar word in hotels name. Ex - Taj Hotel / Similar"
									/>
									<Grid gap="4">
										<Col sm={6} paddingX="0" md={3}>
											<Stack gap="1">
												<SelectField
													select={Select}
													name="hotel_variation"
													label="Hotel Card Style"
													placeholder="e.g. Left"
													options={hotel_section_settings?.variants}
													clearable={false}
													onChange={(
														e: TEmailPdfTemplateCreatePayload["hotel_variation"]
													) => {
														form.change("hotel_variation", e)
														if (e.id === "image_and_text") {
															form.change(
																"hotel_variation_image_position",
																e?.configurations[0]?.options[0] as never
															)
														}
														// To avoid the scenario where image position is not set for image_and_text variation, and we are accessing configurations[0] without checking the variation id because we have only one variation with configurations for now but in future if we have more variations with different configurations then we need to handle it accordingly
													}}
												/>
												<GetFieldValue<THotelImageVariant> name="hotel_variation">
													{({ value: hotel_variation }) =>
														hotel_variation.id === "image_and_text" && (
															<SelectField
																name="hotel_variation_image_position"
																options={
																	hotel_variation.configurations[0].options
																}
																select={Select}
																label="Image"
																clearable={false}
															/>
														)
													}
												</GetFieldValue>
											</Stack>
										</Col>
										<Col sm={12} paddingX="0" md={7}>
											<GetFieldValue<
												TEmailPdfTemplateCreatePayload["hotel_variation"]
											> name="hotel_variation">
												{({ value: hotel_variation }) => (
													<GetFieldValue<
														TEmailPdfTemplateCreatePayload["hotel_variation_image_position"]
													> name="hotel_variation_image_position">
														{({ value: hotel_variation_image_position }) => (
															<Stack gap="1">
																<Text fontWeight="semibold">
																	Hotel Card Preview
																</Text>
																<Box padding="1" rounded="md" borderWidth="1">
																	{hotel_variation.id === "image_and_text" ? (
																		<HotelPreview
																			alignment={
																				hotel_variation_image_position.id
																			}
																		/>
																	) : (
																		hotel_variation.id === "table_view" && (
																			<HotelPreview
																				alignment={hotel_variation.id}
																			/>
																		)
																	)}
																</Box>
															</Stack>
														)}
													</GetFieldValue>
												)}
											</GetFieldValue>
										</Col>
									</Grid>
								</Stack>
							</Col>
						</Grid>
						<Divider marginY="0" />
						<Grid gap="6">
							<Col sm={12} md={4}>
								<Stack gap="1">
									<Heading fontSize="md">
										Settings for Transfer and Activity Section
									</Heading>
									<Text color="muted">
										Customize the section title, variant and other settings.
									</Text>
								</Stack>
							</Col>
							<Col>
								<Stack gap="4">
									<Component
										initialState={
											transport_and_activity_section_settings.title
												? true
												: false
										}
									>
										{({ state: editing, setState }) =>
											!editing ? (
												<Stack gap="1">
													<Text fontWeight="semibold">Section Title</Text>
													<Inline
														borderWidth="1"
														rounded="md"
														paddingLeft="4"
														bgColor="subtle"
														gap="4"
														justifyContent="between"
														alignItems="center"
													>
														<Text>Using default title settings</Text>
														<Button
															onClick={() => setState(true)}
															level="tertiary"
														>
															<Icons.Pencil />
														</Button>
													</Inline>
												</Stack>
											) : (
												<TextInputField
													name="transport_and_activity_section_settings.title"
													label="Section Title"
													type="text"
													placeholder="e.g Transportation and Activities"
												/>
											)
										}
									</Component>
									<SwitchInputField
										name="transport_and_activity_section_settings.hide_transport_summary_for_same_cabs"
										label="Hide Transport Summary if Same Cab Type is used"
										help="This can be helpful if you always share full itinerary and only want to include cab details. This WON'T be applicable if you have Activities in Quote"
									/>
									<SwitchInputField
										name="transport_and_activity_section_settings.show_activity_start_time"
										label="Show Activity And Transport Start Time"
										help="Select the checkbox to show the activity start time in the generated PDF"
									/>
									{transport_and_activity_section_settings.variants.length >
									1 ? (
										<SelectField
											label="Variant"
											select={Select}
											name="transport_and_activity_section_settings.variant"
											options={transport_and_activity_section_settings.variants}
										/>
									) : null}
									<MarkdownInputField
										name="transport_and_activity_section_settings.terms"
										label="Specific Terms and Notes"
										placeholder="e.g. **Note**: Vehicles will be point-to-point in the mountain areas."
									/>
								</Stack>
							</Col>
						</Grid>
						<Divider marginY="0" />
						<Grid gap="6">
							<Col sm={12} md={4}>
								<Stack gap="1">
									<Heading fontSize="md">Settings for Pricing</Heading>
									<Text color="muted">
										Configure how pricing information is managed and displayed
										across quotes.
									</Text>
								</Stack>
							</Col>
							<Col>
								<Stack gap="4">
									<Stack gap="2">
										<Text fontWeight="semibold">Package Price Location</Text>
										<Inline gap="4" collapseBelow="md">
											<Box flex="1" padding="2" rounded="md" borderWidth="1">
												<RadioInputField
													name="pricing_section_settings.show_package_price_below_hotel"
													label="Show package price separately"
													value="0"
													expandLabel
													help={
														<Stack gap="2" color="default">
															<Divider marginY="0" />
															<Box fontSize="xs">
																<Box
																	fontWeight="semibold"
																	fontSize="sm"
																	backgroundColor="inset"
																	paddingY="1"
																	paddingX="2"
																	marginBottom="1"
																>
																	Hotels
																</Box>
																<Stack paddingLeft="2">
																	<Text>Option 1:</Text>
																	<Stack paddingLeft="3">
																		<Text as="li">
																			Hotel A (1st, 2nd Nights)
																		</Text>
																		<Text as="li">
																			Hotel B (3rd, 4th Nights)
																		</Text>
																	</Stack>
																</Stack>
																<Divider marginY="1" />
																<Stack paddingLeft="2">
																	<Text>Option 2:</Text>
																	<Stack paddingLeft="3">
																		<Text as="li">
																			Hotel C (1st, 2nd Nights)
																		</Text>
																		<Text as="li">
																			Hotel D (3rd, 4th Nights)
																		</Text>
																	</Stack>
																</Stack>
															</Box>
															<Box
																fontWeight="semibold"
																width="full"
																fontSize="sm"
																backgroundColor="inset"
																paddingY="1"
																paddingX="2"
															>
																Total Price
															</Box>
															<Inline paddingLeft="2">
																<Box flex="1">
																	<Text fontWeight="semibold" fontSize="xs">
																		Option 1: <br />
																		xxx
																	</Text>
																</Box>
																<Box flex="1">
																	<Text fontWeight="semibold" fontSize="xs">
																		Option 2: <br />
																		xxx
																	</Text>
																</Box>
															</Inline>
														</Stack>
													}
												/>
											</Box>
											<Box flex="1" padding="2" rounded="md" borderWidth="1">
												<RadioInputField
													name="pricing_section_settings.show_package_price_below_hotel"
													label="Show package price with hotels"
													value="1"
													expandLabel
													help={
														<Stack gap="2" color="default">
															<Divider marginY="0" />
															<Box fontSize="xs">
																<Box
																	fontWeight="semibold"
																	fontSize="sm"
																	backgroundColor="inset"
																	paddingY="1"
																	paddingX="2"
																	marginBottom="1"
																>
																	Hotels
																</Box>
																<Stack paddingLeft="2" marginBottom="1">
																	<Text>Option 1:</Text>
																	<Stack paddingLeft="4">
																		<Text as="li">
																			Hotel A (1st, 2nd Nights)
																		</Text>
																		<Text as="li">
																			Hotel B (3rd, 4th Nights)
																		</Text>
																	</Stack>
																</Stack>
																<Box fontWeight="semibold" paddingLeft="2">
																	Total Price
																	<br />
																	xxx
																</Box>
															</Box>
															<Divider marginY="0" />
															<Box fontSize="xs">
																<Stack paddingLeft="2" marginBottom="1">
																	<Text>Option 2:</Text>
																	<Stack paddingLeft="4">
																		<Text as="li">
																			Hotel C (1st, 2nd Nights)
																		</Text>
																		<Text as="li">
																			Hotel D (3rd, 4th Nights)
																		</Text>
																	</Stack>
																</Stack>
																<Box paddingLeft="2" fontWeight="semibold">
																	Total Price
																	<br />
																	xxx
																</Box>
															</Box>
														</Stack>
													}
												/>
											</Box>
										</Inline>
									</Stack>
									<SwitchInputField
										name="pricing_section_settings.hide_quote_price"
										label="Hide Price in Quote"
										help="When enabled, the package price will not be visible in the quote."
									/>
									<SwitchInputField
										name="pricing_section_settings.show_component_wise_prices"
										label="Show Price Breakup (Component wise Total)"
										help="Display component-wise pricing in package"
									/>
								</Stack>
							</Col>
						</Grid>
						{daywise_schedule_section_settings ? (
							<>
								<Divider marginY="0" />
								<Grid gap="6">
									<Col sm={12} md={4}>
										<Stack gap="1">
											<Heading fontSize="md">
												Settings for Day Wise Itinerary Section
											</Heading>
											<Text color="muted">
												Customize the settings of Day-wise Itinerary section
											</Text>
										</Stack>
									</Col>
									<Col>
										<Stack gap="4">
											<TextInputField
												label="Itinerary Section Title"
												name="daywise_schedule_section_settings.title"
												help="Leave blank to remove the section title"
												placeholder="e.g. Day Wise Itinerary"
											/>
											<SwitchInputField
												name="daywise_schedule_section_settings.hide_travel_distance"
												label="Hide Approx. Travel Distance from Itinerary"
												help="This can be helpful if the travel distance includes extra safe running kms which should not be shared."
											/>
											<SwitchInputField
												name="daywise_schedule_section_settings.show_same_day_service_together"
												label="Show Services Together"
												help="Select the checkbox to show the services together."
											/>
											<Grid gap="4">
												<Col sm={6} md={3}>
													<Stack gap="2">
														<SelectField
															select={Select}
															name="daywise_variation"
															label="Itinerary Style"
															placeholder="e.g. Full Width"
															options={
																daywise_schedule_section_settings.variants
															}
															clearable={false}
															onChange={(e) => {
																form.change("daywise_variation", e)
																form.change(
																	"daywise_variation_image_position",
																	e?.configurations[0]?.options[0] as never
																)
															}}
														/>
														<GetFieldValue<
															TEmailPdfTemplateCreatePayload["daywise_variation"]
														> name="daywise_variation">
															{({ value: daywise_variation }) =>
																daywise_variation?.configurations?.length && (
																	<SelectField
																		select={Select}
																		name="daywise_variation_image_position"
																		label="Image"
																		clearable={false}
																		options={
																			daywise_variation.configurations[0]
																				.options
																		}
																	/>
																)
															}
														</GetFieldValue>
													</Stack>
												</Col>
												<Col sm={12} md={7}>
													<Stack gap="1">
														<Text fontWeight="semibold">Itinerary Preview</Text>
														<Stack padding="2" rounded="md" borderWidth="1">
															<DaywisePreviewField />
														</Stack>
													</Stack>
												</Col>
											</Grid>
										</Stack>
									</Col>
								</Grid>
							</>
						) : null}
						<Divider marginY="0" />
						<Grid gap="6">
							<Col sm={12} md={4}>
								<Stack gap="1">
									<Heading fontSize="md">
										Show, Hide & Reorder PDF Sections
									</Heading>
									<Text color="muted">
										⁠Choose which sections appear in the PDF and set their
										order. Move sections to the right in the sequence you want
										them to appear. Sections left on the left will be hidden
										from the PDF.
									</Text>
								</Stack>
							</Col>
							<Col>
								<Box flex="1">
									<Stack gap="4">
										<GetFieldValue<typeof section_ordering_settings>
											name={"section_ordering_settings"}
										>
											{({ value: sections }) => {
												return (
													<>
														{(sections || section_ordering_settings)?.map(
															(section, index) =>
																section.hide === 0 && (
																	<Inline
																		gap="2"
																		alignItems={"center"}
																		key={index}
																	>
																		<Stack
																			size={"6"}
																			alignItems="center"
																			justifyContent="center"
																			borderWidth="1"
																			rounded="full"
																			bgColor="success_emphasis"
																			color="on_emphasis"
																		>
																			<Text fontWeight="semibold">
																				{index + 1}
																			</Text>
																		</Stack>
																		<Stack flex="1" minWidth="0" gap="2">
																			<Box
																				paddingX={"4"}
																				paddingY={"2"}
																				borderWidth="1"
																				rounded="md"
																				maxWidth={"xs"}
																			>
																				<Inline
																					gap="4"
																					alignItems={"center"}
																					justifyContent="between"
																				>
																					<Text>{section.title}</Text>
																				</Inline>
																			</Box>
																			<GetFieldValue name="pricing_section_settings.show_package_price_below_hotel">
																				{({ value }) => {
																					return section.title ===
																						"Total Price" && isTruthy(value) ? (
																						<Box
																							color="warning"
																							borderColor="warning"
																							backgroundColor="warning"
																							borderWidth="1"
																							borderRadius="md"
																							maxWidth="xs"
																							paddingX="2"
																							paddingY="1"
																							fontSize="sm"
																						>
																							<Icons.Info size="4" /> Total
																							Price will be shown with the
																							Hotels based on your pricing
																							settings.
																						</Box>
																					) : null
																				}}
																			</GetFieldValue>
																		</Stack>
																	</Inline>
																)
														)}
														<Box flex="1" paddingLeft={"8"}>
															<GetFieldValue name="pricing_section_settings.show_package_price_below_hotel">
																{({ value }) => (
																	<ReorderSections
																		pricingSectionSettings={isTruthy(value)}
																		sections={sections}
																		onSuccess={(value) => {
																			form.change(
																				"section_ordering_settings",
																				value
																			)
																		}}
																		allowHide
																	>
																		{({ reOrder }) => (
																			<Button
																				status="primary"
																				onClick={reOrder}
																			>
																				<Icons.SwitchHorizontal rotate="90" />
																				Manage Sections
																			</Button>
																		)}
																	</ReorderSections>
																)}
															</GetFieldValue>
														</Box>
													</>
												)
											}}
										</GetFieldValue>
									</Stack>
								</Box>
							</Col>
						</Grid>
						<Divider marginY="0" />
						<Grid gap="6">
							<Col sm={12} md={4}>
								<Stack gap="1">
									<Heading fontSize="md">
										Settings for Important Notes Section
									</Heading>
									<Text color="muted">Customize the section title.</Text>
								</Stack>
							</Col>
							<Col>
								<Stack gap="4">
									<TextInputField
										name="other_info_section_settings.title"
										label="Section Title"
										type="text"
										placeholder="e.g Important Information"
									/>
								</Stack>
							</Col>
						</Grid>
						<Divider marginY="0" />
						<Grid gap="6">
							<Col sm={12} md={4}>
								<Stack gap="1">
									<Heading fontSize="md">Settings for TnC. Section</Heading>
									<Text color="muted">Customize the section title.</Text>
								</Stack>
							</Col>
							<Col>
								<Stack gap="4">
									<TextInputField
										name="terms_section_settings.title"
										label="Section Title"
										type="text"
										placeholder="e.g Terms and Conditions"
									/>
								</Stack>
							</Col>
						</Grid>
						<Grid gap="6">
							<Col sm={12} md={4}>
								<Stack gap="1">
									<Heading fontSize="md">
										Settings for Inclusions/Exclusions Section
									</Heading>
									<Text color="muted">
										Customize the inclusions/exclusions title.
									</Text>
								</Stack>
							</Col>
							<Col>
								<TextInputField
									name="inclusions_exclusions_section_settings.title"
									label="Inclusions / Exclusions Title"
									type="text"
									placeholder="e.g Inclusions and Exclusions"
								/>
							</Col>
						</Grid>
						<Divider marginY="0" />
						<Grid gap="6">
							<Col sm={12} md={4}>
								<Stack gap="1">
									<Heading fontSize="md">Settings for Sales Title</Heading>
									<Text color="muted">
										Customize the section title. For example: Your Travel
										Expert, Planned by, Managed by, Package Created by
									</Text>
								</Stack>
							</Col>
							<Col>
								<Stack gap="4">
									<TextInputField
										name="hero_section_settings.sales_info_title"
										label="Sales Title"
										type="text"
										placeholder="created by"
									/>
								</Stack>
							</Col>
						</Grid>
						<Divider marginY="0" />

						<Grid gap="0">
							<Col sm={12} md={{ offset: 4, span: 8 }}>
								<Stack gap="4" paddingLeft={{ md: "6" }}>
									<SubmissionError />
									<Inline gap="4">
										<Button type="submit" disabled={submitting}>
											{submitting ? "Please wait..." : "Save Template"}
										</Button>
										<Button
											disabled={submitting}
											onClick={() => onCancel()}
											level="tertiary"
										>
											Cancel
										</Button>
									</Inline>
								</Stack>
							</Col>
						</Grid>
					</Stack>
				</form>
			)}
		</Form>
	)
}

function GetBorderWidthFieldValue({
	settingsName,
	children,
}: {
	settingsName: string
	children: (props: {
		borderWidth: [top: number, right: number, bottom: number, left: number]
		hasBorder: boolean
		onChange: (borderWidth: number) => void
	}) => React.ReactNode
}) {
	const { value: top, onChange: changeTop } = useFieldValue<number | "">(
		settingsName + ".borderTopWidth"
	)
	const { value: right, onChange: changeRight } = useFieldValue<number | "">(
		"settings.borderRightWidth"
	)
	const { value: bottom, onChange: changeBottom } = useFieldValue<number | "">(
		"settings.borderBottomWidth"
	)
	const { value: left, onChange: changeLeft } = useFieldValue<number | "">(
		"settings.borderLeftWidth"
	)
	const { borderWidth, hasBorder } = useMemo(() => {
		const border = [
			Number(top || 0),
			Number(right || 0),
			Number(bottom || 0),
			Number(left || 0),
		] as [number, number, number, number]
		return { borderWidth: border, hasBorder: Math.max(...border) > 0 }
	}, [top, right, bottom, left])
	const form = useForm()
	const onChange = useCallback(
		(borderWidth: number) => {
			form.batch(() => {
				changeTop(borderWidth)
				changeRight(borderWidth)
				changeBottom(borderWidth)
				changeLeft(borderWidth)
			})
		},
		[form, changeTop, changeRight, changeBottom, changeLeft]
	)
	return <>{children({ borderWidth: borderWidth, hasBorder, onChange })}</>
}

export function HotelPreview({
	alignment,
}: {
	alignment: THotelImagePlacementOptions["id"] | THotelTableVariant["id"]
}) {
	if (alignment === "right") {
		return (
			<Inline padding="2" gap="2">
				<HotelDetails />
				<HotelImage />
			</Inline>
		)
	}

	if (alignment === "left") {
		return (
			<Inline padding="2" gap="3">
				<HotelImage />
				<Box width="full">
					<HotelDetails />
				</Box>
			</Inline>
		)
	}

	if (alignment === "table_view") {
		return (
			<Table
				responsive
				fontSize="sm"
				headers={["Night", "City", "Hotel Name", "Meal Plans", "Accommodation"]}
				backgroundColor="accent_emphasis"
				rows={[
					[
						<Text>1st Night</Text>,
						<Text>City 1</Text>,
						<Text>Hotel 1</Text>,
						<Text>CP</Text>,
						<Text>Deluxe Room</Text>,
					],
					[
						<Text>2nd Night</Text>,
						<Text>City 2</Text>,
						<Text>Hotel 2</Text>,
						<Text>MAP</Text>,
						<Text>Super Deluxe Room</Text>,
					],
				]}
			/>
		)
	}
}

function HotelImage() {
	return (
		<Box
			display="flex"
			justifyContent="center"
			alignItems="center"
			borderRadius="md"
			bgColor="subtle"
			as="label"
			color="muted"
			htmlFor="file"
			paddingX="4"
			height="full"
			width="1/3"
		>
			<Icons.Photograph style={{ width: "100px", height: "100px" }} />
		</Box>
	)
}

function HotelDetails() {
	return (
		<Stack gap="1" width="full">
			<Box fontSize="sm" paddingY="1" width="full" gap="2">
				<Box
					as="span"
					borderRadius="md"
					padding="1"
					backgroundColor="inset"
					fontWeight="semibold"
				>
					Day
				</Box>
				<Box as="span" fontSize="sm">
					Night at
				</Box>
				<Box as="span" fontSize="sm" fontWeight="semibold">
					City Name
				</Box>
			</Box>

			<Box fontSize="sm" width="1/2">
				Check-in Date
			</Box>

			<Box fontSize="sm" paddingY="1" width="full" fontWeight="semibold">
				<Text>Hotel Name</Text>
				<Stars stars={4} />
			</Box>

			<Inline gap="4">
				<Stack width="full">
					<Box fontSize="sm" paddingY="1">
						<Text fontSize="xs">Rooms</Text>
						<Text fontWeight="semibold">Rooms Type</Text>
						<Text>No of PAX</Text>
					</Box>
				</Stack>

				<Stack width="full">
					<Box fontSize="sm" paddingY="1">
						<Text fontSize="xs">Meals Plan</Text>
						<Text fontWeight="semibold">Meal Type</Text>
					</Box>
				</Stack>
			</Inline>
		</Stack>
	)
}

function DaywisePreviewField() {
	const { value: imagePosition } = useFieldValue<
		TImageOptions | TImageStackedOptions
	>("daywise_variation_image_position")

	const { value: daywiseSetting } = useFieldValue<
		TEmailPdfTemplateCreatePayload["daywise_schedule_section_settings"]
	>("daywise_schedule_section_settings")

	const servicesTogether = Boolean(
		Number(daywiseSetting?.show_same_day_service_together)
	)

	return (
		<DaywisePreview
			alignment={imagePosition?.id}
			servicesTogether={servicesTogether}
		/>
	)
}

export function DaywisePreview({
	alignment,
	servicesTogether,
}: {
	alignment: TImageOptions["id"] | TImageStackedOptions["id"]
	servicesTogether: boolean
}) {
	switch (alignment) {
		case "align_to_service":
			return (
				<StackedPreview
					servicesTogether={servicesTogether}
					type="align_to_service"
				/>
			)

		case "full_width":
			return (
				<StackedPreview servicesTogether={servicesTogether} type="full_width" />
			)

		case "right":
			return (
				<SideBySidePreview
					servicesTogether={servicesTogether}
					imagePosition="right"
				/>
			)

		case "left":
			return (
				<SideBySidePreview
					servicesTogether={servicesTogether}
					imagePosition="left"
				/>
			)

		case "alternate":
			return (
				<SideBySidePreview
					servicesTogether={servicesTogether}
					imagePosition="alternate"
				/>
			)

		default:
			return null
	}
}

function StackedPreview({
	servicesTogether,
	type,
}: {
	servicesTogether: boolean
	type: "align_to_service" | "full_width"
}) {
	return (
		<Stack gap="1">
			<Inline gap="1">
				<DayBadge />
				<ServiceHeader multipleNames={servicesTogether} />
			</Inline>
			<Stack
				gap="1"
				paddingLeft={type === "align_to_service" ? "12" : undefined}
			>
				<ServiceContent hideServiceName={servicesTogether} />
			</Stack>
		</Stack>
	)
}

function SideBySidePreview({
	servicesTogether,
	imagePosition,
}: {
	servicesTogether: boolean
	imagePosition: "right" | "left" | "alternate"
}) {
	return (
		<Stack gap="1">
			<Inline gap="1">
				<DayBadge />
				<ServiceHeader
					multipleNames={servicesTogether}
					hideServiceName={!servicesTogether}
				/>
			</Inline>
			<Stack gap="1">
				<ServiceDetailsRow
					hideServiceName={servicesTogether}
					imagePosition={imagePosition === "alternate" ? "left" : imagePosition}
				/>
				<ServiceDetailsRow
					hideServiceName={servicesTogether}
					imagePosition={
						imagePosition === "alternate" ? "right" : imagePosition
					}
				/>
			</Stack>
		</Stack>
	)
}

function DayBadge() {
	return (
		<Box
			borderRadius="md"
			paddingY="2"
			paddingX="3"
			fontSize="sm"
			fontWeight="semibold"
			backgroundColor="inset"
		>
			Day
		</Box>
	)
}

function ServiceHeader({
	multipleNames,
	hideServiceName,
}: {
	multipleNames: boolean
	hideServiceName?: boolean
}) {
	return (
		<Stack gap="1" justifyContent="center">
			<Text fontSize="sm">Date</Text>
			{hideServiceName ? null : <ServiceName multipleNames={multipleNames} />}
		</Stack>
	)
}

function ServiceName({ multipleNames }: { multipleNames: boolean }) {
	return (
		<Text fontSize="sm" fontWeight="semibold">
			Service Name {multipleNames && " + Service Name"}
		</Text>
	)
}

function ServiceDescription() {
	return (
		<Text fontSize="xs">
			Detailed description of the service, including highlights and important
			information.
		</Text>
	)
}

function ServiceImage({
	size = "50%",
	widthFull,
}: {
	size?: string
	widthFull?: boolean
}) {
	return (
		<Box
			width={widthFull ? "full" : "1/2"}
			height="16"
			display="flex"
			justifyContent="center"
			alignItems="center"
			backgroundColor="inset"
			borderRadius="lg"
		>
			<Icons.Photograph
				style={{
					width: size,
					height: size,
				}}
				color="subtle"
			/>
		</Box>
	)
}

function ServiceContent({
	hideServiceName,
	imageSize = "60%",
}: {
	hideServiceName: boolean
	imageSize?: string
}) {
	return (
		<>
			<ServiceImage size={imageSize} widthFull />
			<ServiceDescription />
			{hideServiceName ? null : <ServiceName multipleNames={false} />}
			<ServiceImage size={imageSize} widthFull />
			<ServiceDescription />
		</>
	)
}

function ServiceDetailsRow({
	hideServiceName,
	imagePosition,
	imageSize = "60%",
}: {
	hideServiceName: boolean
	imagePosition: "left" | "right"
	imageSize?: string
}) {
	return (
		<Inline gap="2" justifyContent="between">
			{imagePosition === "left" && <ServiceImage size={imageSize} />}
			<Stack gap="1">
				{hideServiceName ? null : <ServiceName multipleNames={false} />}
				<ServiceDescription />
			</Stack>
			{imagePosition === "right" && <ServiceImage size={imageSize} />}
		</Inline>
	)
}

export function PackagePreviewTableView() {
	return (
		<Table
			responsive
			bordered
			fontSize="sm"
			backgroundColor="accent_emphasis"
			rows={[
				[<Text fontWeight="semibold">Trip ID</Text>, <Text>Trip ID</Text>],
				[
					<Text fontWeight="semibold">Destination</Text>,
					<Stack>
						<Text>Destination State</Text>
						<Text fontSize="xs">Destination Name, No. of Nights</Text>
					</Stack>,
				],
				[
					<Text fontWeight="semibold">Start Date</Text>,
					<Text>Trip Start Date</Text>,
				],
				[
					<Text fontWeight="semibold">Trip Duration</Text>,
					<Text>No. Of Nights / No. of Days</Text>,
				],
				[<Text fontWeight="semibold">Pax</Text>, <Text>No. of Pax</Text>],
				[
					<Text fontWeight="semibold">Reference ID</Text>,
					<Text>Reference ID</Text>,
				],
			]}
		/>
	)
}

export function PackagePreviewDefaultView() {
	return (
		<Stack borderWidth="1" padding="2" gap="2" rounded="md">
			<Inline gap="6" fontSize="sm">
				<Stack gap="4">
					<Box>
						<Box fontWeight="semibold">DESTINATION</Box>
						<Box>Destination Name</Box>
					</Box>
					<Box>
						<Box fontWeight="semibold">PAX</Box>
						<Box>No. of Adults, children(Age)</Box>
					</Box>
				</Stack>

				<Stack gap="4">
					<Box>
						<Box fontWeight="semibold">START DATE</Box>
						<Box>Start Date</Box>
					</Box>
					<Box>
						<Box fontWeight="semibold">TRIP ID</Box>
						<Box>TRIP 123</Box>
					</Box>
				</Stack>
				<Stack>
					<Box fontWeight="semibold">DURATION</Box>
					<Box>No. of Nights/ No. of Days</Box>
				</Stack>
			</Inline>
			<Inline gap="2" fontSize="sm">
				<Stack></Stack>
				<Stack></Stack>
			</Inline>
		</Stack>
	)
}
